export interface Site {
    id: string;
    name: string;
}

export interface Device {
    id: string;
    site_id: string;
    name: string;
    interfaces: DeviceInterface[];
    quotas?: Quota[];
}

export interface DeviceInterface {
    alias: string;
    wan_type: string;
}

export interface Quota {
    id?: string;
    site_id: string;
    site_name?: string;
    device_id: string;
    device_name?: string;
    wan_links?: string[];
    access_networks?: string[];
    access_networks_name?: string[];
    access_network_wan_scope?: string[];
    starlink_quota?: StarlinkQuota;
    limit: number;
    period: number;
    period_unit: string;
    period_display?: string;
    start_time: string;
    actions?: Action[];
    current_quota_start_time?: string;
    current_usage?: string;
    current_usage_updated?: string;
    used_percent?: string;
    span?: string;
    estimated_max_over_date?: string | null;
    status?: string;
    notification_methods?: string[],
    users_to_notify?: User[];
    disabled?: boolean;
    quota_type?: string;
    captive_usage_type?: string;
    access_level?: string;
    // floor-only from here: null or absent on a ceiling, and on an API older than floors
    direction?: QuotaDirection;
    recovery_windows?: number;
    warmup_windows?: number;
    suppression?: QuotaSuppression | null;
    healthy_window_count?: number;
    last_window_end?: string | null;
    suppression_reason?: string | null;
    window_history?: QuotaWindow[] | null;
}

export type QuotaDirection = 'ceiling' | 'floor';

export interface QuotaSuppression {
    idle_site?: boolean;
}

// One judged window, oldest first. No reason means the window counted; withheld means
// it breached but its actions were dropped because fresher usage showed the link moving
export interface QuotaWindow {
    end: string;
    bytes: number;
    reason?: string;
    withheld?: boolean;
}

export interface StarlinkQuota {
    service_line_numbers: string[];
    enabled: boolean;
}

export interface User {
    name: string;
    email: string;
}
export interface Action {
    id: string;
    usage_percentage: number;
    type: ActionType;
    access_networks?: string[];
    wan_profile_id?: string;
    traffic_policy_id?: string;
    interfaces?: string[];
    device_target?: string;
    device_target_type?: string;
    device_quota_bytes?: number;
    executed: boolean;
    valid?: boolean;
    revert?: boolean;
    // floor-only: windows in a row the tier needs, and how many it has so far
    consecutive_windows?: number;
    breach_window_count?: number;
    executed_at?: string | null;
}

export type ActionType = 'assignWanProfile' | 'assignTrafficPolicy' | 'disableInterfaces' | 'enableInterfaces' | 'sendNotification' | 'predictOverageNotification' | 'resetDevice' | 'assignDevicePolicy' | 'removeDevicePolicy' | '';

export interface AccessNetwork {
    id: string;
    name: string;
    wan_profile: string;
    vlan_aggregate: string;
    captive: boolean;
}

export const Actions = {
    ActionAssignWanProfile: 'assignWanProfile',
    ActionAssignTrafficPolicy: 'assignTrafficPolicy',
    ActionDisableInterface: 'disableInterfaces',
    ActionEnableInterfaces: 'enableInterfaces',
    ActionNotification: 'sendNotification',
    ActionPredictOverageNotification: 'predictOverageNotification',
    ActionResetDevice: 'resetDevice',
    ActionAssignDevicePolicy: 'assignDevicePolicy',
    ActionRemoveDevicePolicy: 'removeDevicePolicy',
    ActionUndefined: ''
} as const;

export interface WanProfile {
    id: string;
    name: string;
}

// GET_TRAFFIC_POLICIES returns both kinds: 'vlan' is a network policy, 'device' a
// device policy. Callers filter with the helpers below.
export const NETWORK_POLICY_TYPE = 'vlan';
export const DEVICE_POLICY_TYPE = 'device';

// An assignment names no client up front: it finds the clients over the per device quota
// and applies the policy to each of their MACs when it fires. A removal can also name one
// of those MACs, to release a single device.
export const DEVICE_TARGET_MAC = 'mac';
export const DEVICE_TARGET_PER_DEVICE_QUOTA = 'per_device_quota';
// the policy goes on the access network itself, where it covers every client on it
export const DEVICE_TARGET_ACCESS_NETWORK = 'access_network';

export interface TrafficPolicy {
    id: string;
    type?: string;
    name: string;
}

export interface StarlinkServiceLine {
    service_line_number?: string;
    nickname?: string;
}

export type QuotaSortBy = 'site_name' | 'device_name' | 'wan_links' | 'current_quota_start_time' | 'period_display' | 'used_percent' | 'status'
export type SortOrder = 'asc' | 'desc'

export const NotificationMethods = {
    'email': 'Email',
    'microsoft_teams': 'Microsoft Teams',
}

export const CAPTIVE_USAGE_AUTHENTICATED = 'authenticated';
export const CAPTIVE_USAGE_ANONYMOUS = 'anonymous';

export const DATA_USAGE_TYPE_LABELS: { [dataUsageType: string]: string } = {
    'interface': 'WAN Interface',
    'access_network': 'Access Network',
    'captive_authenticated': 'Captive Authenticated',
    'captive_anonymous': 'Captive Unauthenticated',
    'starlink_service_line': 'Starlink Service Line',
}

// the captive types count access network usage too, and save which captive users they count
export const CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE: { [dataUsageType: string]: string } = {
    'captive_authenticated': CAPTIVE_USAGE_AUTHENTICATED,
    'captive_anonymous': CAPTIVE_USAGE_ANONYMOUS,
}

export const isCaptiveDataUsageType = (dataUsageType: string): boolean => dataUsageType in CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE;

// Assigning to a device picks clients out of the unauthenticated half of a captive
// network, so it has nobody to act on when the quota counts authenticated usage.
// Assigning to the access network covers everyone on it and applies either way.
export const allowsPerDeviceQuotaActions = (dataUsageType: string): boolean => dataUsageType !== 'captive_authenticated';

export const isAccessNetworkDataUsageType = (dataUsageType: string): boolean => dataUsageType === 'access_network' || isCaptiveDataUsageType(dataUsageType);

export const dataUsageTypeOfQuota = (quota: Quota | null): string => {
    if (quota == null) return '';
    if (quota.wan_links?.length) return 'interface';
    if (quota.access_networks?.length) {
        const captiveType = Object.keys(CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE).find(t => CAPTIVE_USAGE_TYPE_BY_DATA_USAGE_TYPE[t] === quota.captive_usage_type);
        return captiveType ?? 'access_network';
    }
    if (quota.starlink_quota?.service_line_numbers?.length) return 'starlink_service_line';
    return '';
}

export const accessNetworkSourceLabel = (captiveUsageType: string | undefined): string => {
    switch (captiveUsageType) {
        case CAPTIVE_USAGE_AUTHENTICATED: return 'Captive Authenticated';
        case CAPTIVE_USAGE_ANONYMOUS: return 'Captive Unauthenticated';
        default: return 'Access Networks';
    }
}

export const WAN_SCOPE_TYPES = [
    'wired',
    'wifi',
    'lte',
    'lband',
    'starlink',
    'starlink_r',
    'oneweb',
    'vsat',
    'vsat_ka',
    'vsat_ku',
    'vsat_highspeed',
    'vsat_unlimited',
    'vsat_voip',
] as const;

export const WAN_TYPE_DISPLAY_LABELS: { [wanType: string]: string } = {
    'wired': 'Ethernet',
    'wifi': 'WIFI',
    'lte': 'Cellular',
    'lband': 'LBand',
    'starlink': 'Starlink',
    'starlink_r': 'Starlink Residential',
    'oneweb': 'OneWeb',
    'vsat': 'VSAT',
    'vsat_ka': 'VSAT Ka-band',
    'vsat_ku': 'VSAT Ku-band',
    'vsat_highspeed': 'VSAT High Speed',
    'vsat_unlimited': 'VSAT Unlimited',
    'vsat_voip': 'VSAT VoIP',
}

export const WAN_TYPE_ALIASES: { [wanType: string]: string[] } = {
    'lte': ['cellular'],
}

export const wanTypeMatches = (wanType: string, deviceWanType: string): boolean =>
    deviceWanType === wanType || (WAN_TYPE_ALIASES[wanType] ?? []).includes(deviceWanType);

export const wanTypeLabel = (wanType: string): string => WAN_TYPE_DISPLAY_LABELS[wanType] ?? wanType;

export const formatWanScope = (scope: string[] | undefined): string => (scope ?? []).map(wanTypeLabel).join(', ');

export const QUOTA_DIRECTION_CEILING: QuotaDirection = 'ceiling';
export const QUOTA_DIRECTION_FLOOR: QuotaDirection = 'floor';

// an API older than floors sends no direction, and every such quota is a ceiling
export const isFloorQuota = (quota: Quota | null | undefined): boolean => quota?.direction === QUOTA_DIRECTION_FLOOR;

const SECONDS_BY_PERIOD_UNIT: { [unit: string]: number } = {
    'm': 60,
    'H': 3600,
    'D': 86400,
};

// floor windows are minutes or hours; units are case-sensitive, 'm' is not 'M'
export const windowLengthSeconds = (quota: Quota): number => (quota.period ?? 0) * (SECONDS_BY_PERIOD_UNIT[quota.period_unit] ?? 0);

export const SUPPRESSION_REASON_LABELS: { [reason: string]: string } = {
    'no_telemetry': 'Device not reporting',
    'interface_down': 'Interface disabled',
    'disabled_by_quota': 'Switched off by another quota',
    'ha_standby': 'HA standby',
    'site_idle': 'No clients connected',
    'maintenance': 'Upgrade or config push in progress',
    'warmup': 'Warming up',
    'quota_too_young': 'First window not complete',
}

// the reason set may grow, so an unknown code still reads as skipped
export const suppressionReasonLabel = (reason: string | null | undefined): string =>
    reason ? (SUPPRESSION_REASON_LABELS[reason] ?? 'Skipped') : '';

const CEILING_STATUS_LABELS: { [status: string]: string } = {
    'active': 'Normal',
    'alerting': 'Over Quota Threshold',
    'exceeded': 'Over Max Quota',
}

const FLOOR_STATUS_LABELS: { [status: string]: string } = {
    'active': 'Above Minimum',
    'alerting': 'Below Minimum',
    'exceeded': 'Under Minimum – Action Taken',
    'unknown': 'Not Evaluated',
}

// window_history may arrive as a JSON string if the encoding ever changes; parse defensively
export const quotaWindowHistory = (quota: Quota): QuotaWindow[] => {
    const history = quota.window_history as QuotaWindow[] | string | null | undefined;
    if (!history) return [];
    if (typeof history === 'string') {
        try { return JSON.parse(history) ?? []; } catch { return []; }
    }
    return history;
}

export type FloorActionOutcome = 'taken' | 'withheld' | 'not_taken';

// exceeded only says a tier's run is complete (site-controller 3eeda76a). An action ran if it
// has a fire time; otherwise the newest window says whether it was withheld, and if not it
// most likely failed (e.g. device unreachable), so nothing may claim the link recovered.
export const floorActionOutcome = (quota: Quota): FloorActionOutcome => {
    if ((quota.actions ?? []).some(a => !a.revert && a.executed_at)) return 'taken';
    const history = quotaWindowHistory(quota);
    return history[history.length - 1]?.withheld ? 'withheld' : 'not_taken';
}

const FLOOR_EXCEEDED_LABELS: { [outcome in FloorActionOutcome]: string } = {
    taken: 'Under Minimum – Action Taken',
    withheld: 'Under Minimum – Action Withheld',
    not_taken: 'Under Minimum – Action Not Taken',
}

// a fixed list: humanize would print 45 minutes and up as "an hour". The API refuses a
// floor window under 15 minutes (site-controller 5536aa96): usage arrives ~11 min late
export const FLOOR_MIN_WINDOW_MINUTES = 15;
export const FLOOR_WINDOW_OPTIONS: { period: number, unit: string }[] = [
    { period: 15, unit: 'm' },
    { period: 20, unit: 'm' },
    { period: 30, unit: 'm' },
    { period: 1, unit: 'H' },
    { period: 2, unit: 'H' },
    { period: 4, unit: 'H' },
];

export const FLOOR_DEFAULT_WINDOW = FLOOR_WINDOW_OPTIONS[0];
export const FLOOR_DEFAULT_LIMIT = 1000 * 1000 * 1000;
export const FLOOR_DEFAULT_RECOVERY_WINDOWS = 2;
export const FLOOR_DEFAULT_WARMUP_WINDOWS = 1;
// Engine constant, not an API field (D9): recovery needs windows at or above this percent
// of the minimum. site-controller will add a contract field if it becomes tunable (QMU-06).
export const FLOOR_RECOVERY_DEADBAND_PERCENT = 110;
// P5: the window_history ring holds 12, so the UI offers no longer run than it can chart
export const FLOOR_MAX_CONSECUTIVE_WINDOWS = 12;

// WAN usage settles about 11 minutes after a bucket opens (measured by site-controller,
// 2026-10-13), so every floor decision trails real time by that much
export const FLOOR_DATA_LAG_MINUTES = 11;

export const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const rest = Math.round(minutes % 60);
    if (hours === 0) return `${rest} min`;
    const hourText = `${hours} hour${hours > 1 ? 's' : ''}`;
    return rest === 0 ? hourText : `${hourText} ${rest} min`;
}

export const formatWindow = (period: number, unit: string): string =>
    formatMinutes(period * (SECONDS_BY_PERIOD_UNIT[unit] ?? 0) / 60);

const ACTION_TYPE_LABELS: { [type: string]: string } = {
    'assignWanProfile': 'Assign WAN Profile',
    'assignTrafficPolicy': 'Assign Network Policy',
    'assignDevicePolicy': 'Assign Device Policy',
    'removeDevicePolicy': 'Remove Device Policy',
    'disableInterfaces': 'Disable Interfaces',
    'enableInterfaces': 'Enable Interfaces',
    'sendNotification': 'Send Notification',
    'predictOverageNotification': 'Predict Overage Notification',
    'resetDevice': 'Reset Device',
}

export const actionTypeLabel = (type: string): string => ACTION_TYPE_LABELS[type] ?? type;

// empty for a status the row has never shown a label for, e.g. unknown on a ceiling
export const quotaStatusLabel = (quota: Quota): string => {
    if (isFloorQuota(quota) && quota.status === 'exceeded') return FLOOR_EXCEEDED_LABELS[floorActionOutcome(quota)];
    const labels = isFloorQuota(quota) ? FLOOR_STATUS_LABELS : CEILING_STATUS_LABELS;
    return labels[quota.status ?? ''] ?? '';
}

// P3: a new window keeps each tier's duration, not its count, rounding up to whole windows.
// Saved actions are remove-and-replace, so they keep their count and get a note instead.
export const rescaleFloorActions = (actions: Action[], oldMinutes: number, newMinutes: number, lockedIds: Set<string>): { actions: Action[], notes: string[] } => {
    const notes: string[] = [];
    const rescaled = actions.map(action => {
        const windows = action.consecutive_windows ?? 1;
        const duration = windows * oldMinutes;
        if (lockedIds.has(action.id)) {
            if (newMinutes !== oldMinutes) notes.push(`${actionTypeLabel(action.type)} keeps ${windows} window${windows > 1 ? 's' : ''}, now ${formatMinutes(windows * newMinutes)} (was ${formatMinutes(duration)}). Remove and re-add it to change that.`);
            return action;
        }
        // never capped at FLOOR_MAX_CONSECUTIVE_WINDOWS: shortening a reset tier would fire it early
        const next = Math.max(1, Math.ceil(duration / newMinutes));
        if (next * newMinutes !== duration) notes.push(`${actionTypeLabel(action.type)} now waits ${formatMinutes(next * newMinutes)} (was ${formatMinutes(duration)}), the nearest whole number of windows.`);
        return next === windows ? action : { ...action, consecutive_windows: next };
    });
    return { actions: rescaled, notes };
}
